import * as Locale from './locale';
import Formsy from 'formsy-react';
import * as phoneNumber from 'google-libphonenumber';

/**
 * Set of generic form validations (usable by UI forms) for common inputs.
 * Each key is a unique data type used in multiple forms and has logic
 * for specifying how the data type is validated in a form.
 *
 * validator: set of validation rules for the form input
 * messages: function for returning translated messages for the validation
 * errors, if triggered
 *
 * @type Object
 */
export var Validations = {
// the available validation rules as specified in validator objects are defined
// in Formsy source:
// https://github.com/christianalfoni/formsy-react/blob/master/src/validationRules.js
    deviceId: {
        validator: {
            matchRegexp: /^[a-z0-9\-_]+$/i,
            minLength: 1,
            maxLength: 100,
        },
        messages: (textForKey) => ({
            matchRegexp: textForKey(Locale.TEXT_FORM_ERROR_INVALID_DEVICE_ID),
            minLength: textForKey(Locale.TEXT_FORM_ERROR_TOO_SHORT, {length: 1}),
            maxLength: textForKey(Locale.TEXT_FORM_ERROR_TOO_LONG, {length: 100}),
        })
    },
    premisesName: {
        validator: {
            minLength: 1,
            maxLength: 255,
        },
        messages: (textForKey) => ({
            minLength: textForKey(Locale.TEXT_FORM_ERROR_TOO_SHORT, {length: 1}),
            maxLength: textForKey(Locale.TEXT_FORM_ERROR_TOO_LONG, {length: 255}),
        })
    },
    address: {
        validator: {
            minLength: 1,
            maxLength: 255,
        },
        messages: (textForKey) => ({
            minLength: textForKey(Locale.TEXT_FORM_ERROR_TOO_SHORT, {length: 1}),
            maxLength: textForKey(Locale.TEXT_FORM_ERROR_TOO_LONG, {length: 255}),
        })
    },
    postalCode: {
        validator: {
            minLength: 1,
            maxLength: 16,
        },
        messages: (textForKey) => ({
            minLength: textForKey(Locale.TEXT_FORM_ERROR_TOO_SHORT, {length: 1}),
            maxLength: textForKey(Locale.TEXT_FORM_ERROR_TOO_LONG, {length: 16}),
        })
    },
    region: {
        validator: {
            minLength: 1,
            maxLength: 255,
        },
        messages: (textForKey) => ({
            minLength: textForKey(Locale.TEXT_FORM_ERROR_TOO_SHORT),
            maxLength: textForKey(Locale.TEXT_FORM_ERROR_TOO_LONG, {length: 255}),
        })
    },
    country: {
        validator: {
            minLength: 1,
            maxLength: 255,
        },
        messages: (textForKey) => ({
            minLength: textForKey(Locale.TEXT_FORM_ERROR_TOO_SHORT),
            maxLength: textForKey(Locale.TEXT_FORM_ERROR_TOO_LONG, {length: 255}),
        })
    },
    email: {
        validator: {
            maxLength: 255,
            requireOneFrom: ['mobile'],
            matchRegexp: /^[-a-z0-9~!$%^&*_=+}{\'?]+(\.[-a-z0-9~!$%^&*_=+}{\'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i
        },
        messages: (textForKey) => ({
            maxLength: textForKey(Locale.TEXT_FORM_ERROR_TOO_LONG, {length: 255}),
            matchRegexp: textForKey(Locale.TEXT_FORM_ERROR_INVALID_EMAIL),
        })
    },
    mobile: {
        validator: {
            validPhoneNumber: ['mobile'],
            requireOneFrom: ['email']
        },
        messages: (textForKey) => ({
            matchRegexp: textForKey(Locale.TEXT_FORM_ERROR_INVALID_MOBILE)
        })
    },
    userSettingsEmail: {
        validator: {
            maxLength: 255,
            matchRegexp: /^[-a-z0-9~!$%^&*_=+}{\'?]+(\.[-a-z0-9~!$%^&*_=+}{\'?]+)*@([a-z0-9_][-a-z0-9_]*(\.[-a-z0-9_]+)*\.(aero|arpa|biz|com|coop|edu|gov|info|int|mil|museum|name|net|org|pro|travel|mobi|[a-z][a-z])|([0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}))(:[0-9]{1,5})?$/i
        },
        messages: (textForKey) => ({
            maxLength: textForKey(Locale.TEXT_FORM_ERROR_TOO_LONG, {length: 255}),
            matchRegexp: textForKey(Locale.TEXT_FORM_ERROR_INVALID_EMAIL),
        })
    },
    userSettingsMobile: {
        validator: {
            validPhoneNumber: ['mobile']
        },
        messages: (textForKey) => ({
            matchRegexp: textForKey(Locale.TEXT_FORM_ERROR_INVALID_MOBILE)
        })
    },
    iccid: {
        validator: {
            matchRegexp: /^89\d{17,18}$/
        },
        messages: (textForKey) => ({
            matchRegexp: textForKey(Locale.TEXT_FORM_ERROR_INVALID_ICCID)
        })
    }
};

/**
 * Validate that a phone number is an empty string or can be formatted in E.164 Phone Number Formatting
 * https://support.twilio.com/hc/en-us/articles/223183008-Formatting-International-Phone-Numbers
 * @param value
 */
export let isValidPhoneNumber = (value) => {
    let valid = false;
    value = value ? value.trim() : '';
    if (value.length === 0) {
        valid = true;
    } else {
        let phoneUtil = phoneNumber.PhoneNumberUtil.getInstance();
        // parsing an invalid number throws exception
        try {
            let number = phoneUtil.parseAndKeepRawInput(value);
            valid = phoneUtil.isValidNumber(number);
        } catch (e) {
            console.error('invalid phone nuumber', e);
        }
    }
    return valid;
};

Formsy.addValidationRule('requireOneFrom', function (values, value, otherFields) {
    // require at least 1 input in a form
    if (value) {
        return true;
    }
    for (let i = 0; i < otherFields.length; i++) {
        if (values[otherFields[i]]) {
            return true;
        }
    }
    return false;
});

Formsy.addValidationRule('validPhoneNumber', (values, value) => {
    return isValidPhoneNumber(value);
});
